import React, { Fragment, useCallback, useEffect, useState } from 'react'

import { Button, Spin } from 'antd'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import CardView from 'pages/records/components/CardView'
import ActivityService from 'api/Activity'
import { timeFormat } from 'utils'
import { ArrowLeftIcon } from 'utils/ui'

import styles from './style.module.scss'

const PAGE_SIZE = 20

// subtype → 문구 키 (백엔드 RESOURCE_HISTORY_SUBTYPES 화이트리스트와 1:1.
// gu_updated_record 는 changed_fields 조합 문구라 여기 없음)
const SUBTYPE_LABEL_KEYS = {
  gu_added_record: 'record.history.added',
  sh_shared_record_with_user: 'record.history.sharedWithUser',
  sh_shared_record_with_group: 'record.history.sharedWithGroup',
  sh_shared_record_with_user_and_group: 'record.history.sharedWithUserAndGroup',
  sh_transferred_ownership: 'record.history.transferredOwnership',
}

const getFieldLabel = (t, type) =>
  t([`record.history.fieldTypes.${type}`, 'record.history.fieldTypes.unknown'])

const getItemText = (t, item) => {
  const { subtype, changed_fields: changedFields = [] } = item

  if (SUBTYPE_LABEL_KEYS[subtype] != null) {
    return t(SUBTYPE_LABEL_KEYS[subtype])
  }
  if (changedFields.length === 0) {
    // 컬럼 도입 전 데이터 / changed_fields 미전송 클라이언트(익스텐션 등) 폴백
    return t('record.history.updatedFallback')
  }
  if (changedFields.length <= 2) {
    return t('record.history.changedFields', {
      fields: changedFields.map((type) => getFieldLabel(t, type)).join(', '),
    })
  }
  // 3개 이상은 접는다 — 서버 정렬 규칙상 password 가 항상 맨 앞이라 대표값이 비밀번호
  return t('record.history.changedFieldsCollapsed', {
    first: getFieldLabel(t, changedFields[0]),
    count: changedFields.length - 1,
  })
}

const isPasswordChanged = (item) => (item.changed_fields || []).includes('password')

function RecordHistory(props) {
  const { id, openItem } = props
  const { t } = useTranslation()

  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  const fetchActivities = useCallback(
    async (offset) => {
      setIsLoading(true)
      try {
        const body = await ActivityService.getResourceActivities({
          resourceId: id,
          limit: PAGE_SIZE,
          offset,
        })
        const { total: totalCount, activities } = body.data
        setTotal(totalCount)
        setItems((prev) => (offset === 0 ? activities : [...prev, ...activities]))
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false)
      }
    },
    [id],
  )

  useEffect(() => {
    if (id != null) {
      setItems([])
      setTotal(0)
      fetchActivities(0)
    }
  }, [id, fetchActivities])

  const handleBack = () => openItem('detail', id)

  const header = (
    <Fragment>
      <div>
        <ArrowLeftIcon onClick={handleBack} />
        <b>{t('record.history.title')}</b>
      </div>
      <div />
    </Fragment>
  )

  return (
    <CardView header={header}>
      <div className={classNames(styles.HistoryContainer)}>
        <Spin spinning={isLoading}>
          {items.length === 0 && isLoading === false ? (
            <div className={classNames(styles.empty)}>{t('record.history.empty')}</div>
          ) : (
            <div className={classNames(styles.timeline)}>
              {items.map((item) => (
                <div key={item.id} className={classNames(styles.item)}>
                  <div
                    className={classNames(styles.dot, { [styles.pw]: isPasswordChanged(item) })}
                  />
                  <div
                    className={classNames(styles.text, { [styles.bold]: isPasswordChanged(item) })}
                  >
                    {getItemText(t, item)}
                  </div>
                  <div className={classNames(styles.sub)}>
                    {timeFormat(item.created_at, 'll')}
                    {item.user_name ? ` · ${item.user_name}` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}

          {items.length < total && (
            <Button
              className={classNames(styles.loadMore)}
              onClick={() => fetchActivities(items.length)}
            >
              {t('record.history.loadMore')}
            </Button>
          )}
        </Spin>
      </div>
    </CardView>
  )
}

export default RecordHistory
